import { CheckBadgeIcon, FireIcon } from "@heroicons/react/24/solid";
import { Tooltip, toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, memo, useMemo } from "react";

import { useI18N } from "@sundae/react-hooks";
import { ADA_METADATA } from "../../../constants/cardano.constants";
import TasteTestVerificationTooltip from "../../../i18n/components/taste-test/TasteTestVerificationTooltip";
import {
  TRENDING_TASTE_TEST_THRESHOLD,
  useTasteTestContext,
} from "../../../pages/TasteTestDetail/context";
import { numberI18n } from "../../../utils/number-format";
import { bigintToString } from "../../../utils/string-format";

const TasteTestBadges: FC = () => {
  const { currentLocale, t } = useI18N("taste-test");
  const {
    state: { tasteTest },
  } = useTasteTestContext();

  /**
   * This defines whether a taste test is verified or not.
   * A taste test is verified if the transaction to create it has been confirmed.
   */
  const isVerifiedTasteTest = Boolean(tasteTest?.creationTxHash);

  /**
   * This defines whether a taste test is trending or not.
   * A taste test is trending if more than 100k ADA has been gathered.
   */
  const isTrendingTasteTest = useMemo(() => {
    if (!tasteTest?.historicalBalance?.tokenGathered?.length) return false;
    const historicalBalance = BigInt(
      tasteTest.historicalBalance.tokenGathered[
        tasteTest?.historicalBalance?.tokenGathered.length - 1
      ]
    );
    return historicalBalance > TRENDING_TASTE_TEST_THRESHOLD;
  }, [tasteTest?.historicalBalance]);

  if (!isVerifiedTasteTest && !isTrendingTasteTest) return null;

  return (
    <div className="inline-flex items-center gap-1">
      {isVerifiedTasteTest && (
        <Tooltip
          key="verified"
          trigger={
            <div className="w-fit">
              <CheckBadgeIcon
                className={cx("inline h-6 w-6 cursor-help", toolkitCx.layout.text.success)}
              />
            </div>
          }
          tooltipLabel={<TasteTestVerificationTooltip txHash={tasteTest?.creationTxHash ?? ""} />}
        />
      )}

      {isTrendingTasteTest && (
        <Tooltip
          key="trending"
          trigger={
            <div className="w-fit">
              <FireIcon className={cx("inline h-6 w-6 cursor-help", toolkitCx.layout.text.error)} />
            </div>
          }
          tooltipLabel={t("detail.badges.trending", {
            adaAmount: numberI18n(
              Number(bigintToString(TRENDING_TASTE_TEST_THRESHOLD, ADA_METADATA.decimals)),
              { maximumFractionDigits: ADA_METADATA.decimals },
              currentLocale
            ),
          })}
        />
      )}
    </div>
  );
};

export default memo(TasteTestBadges);
